import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Globe, Plus, Edit2, Save, X, RefreshCw, ExternalLink,
  AlertCircle, CheckCircle, Truck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const PAGES_TYPES = [
  { id:'homepage', url:'/', label:'Accueil', type:'page' },
  { id:'catalogue', url:'/catalogue', label:'Catalogue', type:'page' },
  { id:'boutique', url:'/boutique', label:'Boutique', type:'page' },
  { id:'packs', url:'/packs', label:'Packs', type:'page' },
  { id:'creer-evenement', url:'/creer-evenement', label:'Configurateur', type:'page' },
  { id:'contact', url:'/contact', label:'Contact', type:'page' },
  { id:'mariage-lot', url:'/mariage-lot', label:'Guide Mariage Lot', type:'page' },
  { id:'animations-entreprise', url:'/animations-entreprise-lot', label:'Animations Entreprise', type:'page' },
  { id:'anniversaire-enfant', url:'/anniversaire-enfant-lot', label:'Anniversaire Enfant', type:'page' },
];

function FieldSEO({ label, value, onChange, ok, multiline, rows, maxLength, placeholder }) {
  const borderColor = ok === true ? 'rgba(31,157,122,0.3)' : ok === false ? 'rgba(197,139,25,0.3)' : 'rgba(255,255,255,0.1)';
  return (
    <div>
      <label className="text-xs mb-1 flex items-center gap-1.5" style={{ color: 'var(--admin-muted)' }}>
        {ok !== undefined && (ok
          ? <CheckCircle size={10} style={{ color: 'var(--admin-green)' }}/>
          : <AlertCircle size={10} style={{ color: 'var(--admin-amber)' }}/>)}
        {label}
      </label>
      {multiline ? (
        <textarea value={value || ''} onChange={onChange} rows={rows || 2} maxLength={maxLength}
          className="w-full px-3 py-2 rounded-xl text-sm resize-y"
          style={{ background: 'var(--admin-surface)', border: `1px solid ${borderColor}`, color: 'var(--admin-text)', outline: 'none' }} />
      ) : (
        <Input value={value || ''} onChange={onChange} placeholder={placeholder || ''}
          style={{ background: 'var(--admin-surface)', borderColor, color: 'var(--admin-text)' }} />
      )}
    </div>
  );
}

function SEOPageForm({ pageConfig, existingData, onSave, onCancel }) {
  const [form, setForm] = useState(existingData || {
    titre_seo: '', meta_titre: '', meta_description: '',
    h1: '', h2: [], h3: [], texte_optimise: '',
    mots_cles: [], slug: '', canonical_url: '',
    og_title: '', og_description: '', og_image: '', robots: 'index, follow',
  });
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('meta');
  const token = sessionStorage.getItem('ose_auth') || localStorage.getItem('ose_auth');
  const H = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));

  async function submit() {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND}/api/admin/seo/pages/${pageConfig.id}`, {
        method: 'PUT', headers: H,
        body: JSON.stringify({ ...form, url: pageConfig.url, page_type: pageConfig.type })
      });
      if (!res.ok) throw new Error();
      toast.success('SEO mis à jour !'); onSave();
    } catch { toast.error('Erreur'); }
    setSaving(false);
  }

  const score = [
    form.titre_seo?.length >= 20 && form.titre_seo?.length <= 60,
    form.meta_description?.length >= 120 && form.meta_description?.length <= 160,
    form.h1?.length > 0,
    (form.mots_cles || []).length >= 3,
    form.og_title?.length > 0,
  ].filter(Boolean).length;

  const TABS = [
    { id: 'meta', label: 'Meta & Titres' },
    { id: 'og', label: 'Open Graph' },
    { id: 'contenu', label: 'Contenu' },
    { id: 'avance', label: 'Avancé' },
  ];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" style={{ backdropFilter: 'blur(6px)', background: 'rgba(0,0,0,0.8)' }}
      onClick={e => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="min-h-screen flex items-start justify-center p-4 py-8">
        <div className="w-full max-w-2xl rounded-2xl" style={{ background: 'var(--admin-bg)', border: '1px solid var(--admin-border)' }}>
          <div className="flex items-center justify-between p-5" style={{ borderBottom: '1px solid var(--admin-border-soft)' }}>
            <div>
              <h2 className="font-bold text-lg" style={{ color: 'var(--admin-blue)' }}>SEO — {pageConfig.label}</h2>
              <p className="text-xs" style={{ color: 'var(--admin-faint)' }}>{pageConfig.url}</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                {[1,2,3,4,5].map(i => <div key={i} className="w-2 h-2 rounded-full" style={{ background: i <= score ? '#1F9D7A' : 'rgba(255,255,255,0.1)' }}/>)}
                <span className="text-xs" style={{ color: score >= 4 ? '#1F9D7A' : score >= 2 ? '#C58B19' : '#e53e3e' }}>{score}/5</span>
              </div>
              <button onClick={onCancel} style={{ color: 'var(--admin-muted)' }}><X size={20}/></button>
            </div>
          </div>
          <div className="flex" style={{ borderBottom: '1px solid var(--admin-border-soft)' }}>
            {TABS.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className="px-4 py-3 text-sm font-medium flex-1"
                style={activeTab === tab.id ? { color: 'var(--admin-blue)', borderBottom: '2px solid #7DB1FF' } : { color: 'var(--admin-muted)' }}>
                {tab.label}
              </button>
            ))}
          </div>
          <div className="p-5 space-y-4" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {activeTab === 'meta' && (<>
              <FieldSEO label={`Titre SEO (balise <title>) — ${(form.titre_seo||'').length}/60 car.`}
                value={form.titre_seo} onChange={e => setF('titre_seo', e.target.value)} maxLength={60}
                ok={form.titre_seo?.length >= 20 && form.titre_seo?.length <= 60} />
              <FieldSEO label={`Meta description — ${(form.meta_description||'').length}/160 car.`}
                value={form.meta_description} onChange={e => setF('meta_description', e.target.value)}
                multiline rows={2} maxLength={160}
                ok={form.meta_description?.length >= 120 && form.meta_description?.length <= 160} />
              <FieldSEO label="H1 (titre principal)" value={form.h1} onChange={e => setF('h1', e.target.value)} ok={form.h1?.length > 0} />
              <FieldSEO label="Mots-clés (virgule)" value={(form.mots_cles||[]).join(', ')}
                onChange={e => setF('mots_cles', e.target.value.split(',').map(v => v.trim()).filter(Boolean))}
                ok={(form.mots_cles||[]).length >= 3} />
              <FieldSEO label="Slug URL" value={form.slug} onChange={e => setF('slug', e.target.value)} placeholder={pageConfig.url.slice(1)} />
              {(form.titre_seo || form.meta_description) && (
                <div className="p-4 rounded-xl" style={{ background: '#fff' }}>
                  <p className="text-xs mb-2 font-semibold" style={{ color: '#666' }}>Prévisualisation Google</p>
                  <p className="text-base font-semibold" style={{ color: '#1a0dab' }}>{form.titre_seo || 'Titre SEO'}</p>
                  <p className="text-xs" style={{ color: '#006621' }}>show-evenement.com{form.slug ? `/${form.slug}` : pageConfig.url}</p>
                  <p className="text-sm" style={{ color: '#545454' }}>{form.meta_description || 'Meta description...'}</p>
                </div>
              )}
            </>)}
            {activeTab === 'og' && (<>
              <FieldSEO label="Open Graph titre" value={form.og_title} onChange={e => setF('og_title', e.target.value)} ok={form.og_title?.length > 0} />
              <FieldSEO label="Open Graph description" value={form.og_description} onChange={e => setF('og_description', e.target.value)} multiline rows={2} />
              <FieldSEO label="Open Graph image URL" value={form.og_image} onChange={e => setF('og_image', e.target.value)} />
              {form.og_image && <img src={form.og_image} alt="OG" className="w-full h-40 object-cover rounded-xl" onError={e => e.target.style.display = 'none'} />}
            </>)}
            {activeTab === 'contenu' && (<>
              <div>
                <label className="text-xs mb-1 block" style={{ color: 'var(--admin-muted)' }}>Texte optimisé SEO</label>
                <textarea value={form.texte_optimise || ''} onChange={e => setF('texte_optimise', e.target.value)} rows={6}
                  placeholder="Texte riche en mots-clés..."
                  className="w-full px-3 py-2 rounded-xl text-sm resize-y"
                  style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border)', color: 'var(--admin-text)', outline: 'none' }} />
              </div>
              <FieldSEO label="H2 (titres sections, virgule)" value={(form.h2||[]).join(', ')} onChange={e => setF('h2', e.target.value.split(',').map(v => v.trim()).filter(Boolean))} />
              <FieldSEO label="H3 (sous-titres, virgule)" value={(form.h3||[]).join(', ')} onChange={e => setF('h3', e.target.value.split(',').map(v => v.trim()).filter(Boolean))} />
            </>)}
            {activeTab === 'avance' && (<>
              <FieldSEO label="URL canonique" value={form.canonical_url||''} onChange={e => setF('canonical_url', e.target.value)} />
              <div>
                <label className="text-xs mb-1 block" style={{ color: 'var(--admin-muted)' }}>Directives robots</label>
                <select value={form.robots || 'index, follow'} onChange={e => setF('robots', e.target.value)}
                  className="w-full px-3 py-2 rounded-xl text-sm" style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border)', color: 'var(--admin-text)' }}>
                  <option value="index, follow">index, follow (recommandé)</option>
                  <option value="noindex, follow">noindex, follow</option>
                  <option value="noindex, nofollow">noindex, nofollow</option>
                </select>
              </div>
              <div className="p-3 rounded-xl" style={{ background: 'rgba(93,188,191,0.05)', border: '1px solid rgba(93,188,191,0.15)' }}>
                <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>
                  Sitemap : <a href={`${BACKEND}/api/sitemap.xml`} target="_blank" rel="noreferrer" style={{ color: 'var(--admin-teal)' }}>show-evenement.com/api/sitemap.xml</a>
                  <br/>À soumettre dans Google Search Console.
                </p>
              </div>
            </>)}
          </div>
          <div className="flex gap-3 px-5 py-4" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <Button onClick={submit} disabled={saving} className="flex-1"
              style={{ background: 'rgba(125,177,255,0.2)', color: 'var(--admin-blue)', border: '1px solid rgba(125,177,255,0.3)' }}>
              <Save size={14} className="mr-2"/>{saving ? 'Sauvegarde...' : 'Enregistrer'}
            </Button>
            <Button onClick={onCancel} variant="outline" style={{ borderColor: 'var(--admin-border)', color: 'var(--admin-muted)' }}>Annuler</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminSEO() {
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pages');
  const token = sessionStorage.getItem('ose_auth') || localStorage.getItem('ose_auth');
  const H = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await fetch(`${BACKEND}/api/admin/seo/pages`, { headers: H }).then(r => r.json());
      setPages(d.pages || []);
    } catch {}
    setLoading(false);
  // eslint-disable-next-line
  }, []);

  useEffect(() => { load(); }, [load]);

  const TABS = [
    { id: 'pages', label: 'Pages' },
    { id: 'sitemap', label: 'Sitemap & Robots' },
    { id: 'transporteurs', label: 'Transporteurs' },
  ];

  return (
    <div data-testid="admin-page">
      <div style={{ marginBottom: 20 }}>
        <p className="adm-eyebrow">Paramètres</p>
        <h2 style={{ fontFamily: 'var(--admin-font-display)', fontSize: 26, fontWeight: 700,
          letterSpacing: '-.6px', color: 'var(--admin-text)', marginTop: 4 }}>SEO Global</h2>
        <p style={{ color: 'var(--admin-muted)', fontSize: 13.5, marginTop: 3 }}>Méta-titres, descriptions et balises de chaque page</p>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 24 }} data-testid="admin-seo">
      <div className="flex items-center justify-end flex-wrap gap-3">
        <div className="flex gap-2">
          <a href={`${BACKEND}/api/sitemap.xml`} target="_blank" rel="noreferrer">
            <Button size="sm" variant="outline" style={{ borderColor: 'var(--admin-border)', color: 'rgba(255,255,255,0.6)' }}>
              <ExternalLink size={14} className="mr-1"/> Sitemap
            </Button>
          </a>
          <a href={`${BACKEND}/api/robots.txt`} target="_blank" rel="noreferrer">
            <Button size="sm" variant="outline" style={{ borderColor: 'var(--admin-border)', color: 'rgba(255,255,255,0.6)' }}>
              <ExternalLink size={14} className="mr-1"/> Robots
            </Button>
          </a>
        </div>
      </div>

      <div className="flex gap-1 p-1 rounded-xl" style={{ background: 'rgba(0,0,0,0.3)' }}>
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className="flex-1 px-4 py-2 rounded-lg text-sm font-medium"
            style={activeTab === tab.id ? { background: '#fff', color: 'var(--admin-teal)' } : { color: 'var(--admin-muted)' }}>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'pages' && (
        <div className="space-y-2">
          {PAGES_TYPES.map(page => {
            const existing = pages.find(p => p.page_id === page.id);
            const score = existing ? [existing.titre_seo?.length >= 20, existing.meta_description?.length >= 120, existing.h1?.length > 0, (existing.mots_cles||[]).length >= 3].filter(Boolean).length : 0;
            return (
              <div key={page.id} className="flex items-center justify-between p-4 rounded-xl cursor-pointer hover:opacity-90"
                style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border-soft)' }}
                onClick={() => setSelectedPage(page)}>
                <div>
                  <p className="font-semibold text-sm" style={{ color: 'var(--admin-text)' }}>{page.label}</p>
                  <p className="text-xs" style={{ color: 'var(--admin-faint)' }}>{page.url}</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex gap-1">{[1,2,3,4].map(i => <div key={i} className="w-2 h-2 rounded-full" style={{ background: i <= score ? '#1F9D7A' : 'rgba(255,255,255,0.1)' }}/>)}</div>
                  <span className="text-xs px-2 py-0.5 rounded-full"
                    style={{ background: existing ? 'rgba(31,157,122,0.1)' : 'rgba(255,255,255,0.06)', color: existing ? '#1F9D7A' : 'rgba(255,255,255,0.3)' }}>
                    {existing ? '✓ Configuré' : 'À configurer'}
                  </span>
                  <Edit2 size={13} style={{ color: 'var(--admin-faint)' }}/>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {activeTab === 'sitemap' && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div className="p-5 rounded-2xl" style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-teal-line)' }}>
            <h3 className="font-bold mb-3" style={{ color: 'var(--admin-teal)' }}>Sitemap XML automatique</h3>
            <p className="text-sm mb-4" style={{ color: 'var(--admin-muted)', lineHeight: 1.7 }}>
              Généré automatiquement avec toutes les pages et tous les produits actifs.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <a href={`${BACKEND}/api/sitemap.xml`} target="_blank" rel="noreferrer"
                className="flex items-center gap-2 p-3 rounded-xl text-sm font-semibold"
                style={{ background: 'var(--admin-teal-dim)', color: 'var(--admin-teal)', border: '1px solid var(--admin-teal-line)' }}>
                <ExternalLink size={14}/> Voir sitemap.xml
              </a>
              <a href={`${BACKEND}/api/robots.txt`} target="_blank" rel="noreferrer"
                className="flex items-center gap-2 p-3 rounded-xl text-sm font-semibold"
                style={{ background: 'var(--admin-blue-dim)', color: 'var(--admin-blue)', border: '1px solid rgba(125,177,255,0.2)' }}>
                <ExternalLink size={14}/> Voir robots.txt
              </a>
            </div>
            <div className="mt-4 p-3 rounded-xl" style={{ background: 'var(--admin-gold-dim)', border: '1px solid rgba(212,175,55,0.2)' }}>
              <p className="text-xs font-semibold mb-1" style={{ color: 'var(--admin-gold)' }}>Google Search Console</p>
              <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>
                Soumettez : <strong style={{ color: 'var(--admin-text)' }}>https://show-evenement.com/api/sitemap.xml</strong>
              </p>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'transporteurs' && (
        <div className="space-y-3">
          {[
            { id: 'mondial_relay', nom: 'Mondial Relay', ready: false, desc: 'Point relais & domicile' },
            { id: 'chronopost', nom: 'Chronopost', ready: false, desc: 'Express 24-48h' },
            { id: 'colissimo', nom: 'Colissimo La Poste', ready: false, desc: 'Standard 48-72h' },
            { id: 'show_evenement', nom: 'Livraison Show Événement', ready: true, desc: 'Gérée manuellement' },
          ].map(t => (
            <div key={t.id} className="flex items-center justify-between p-4 rounded-xl"
              style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border-soft)' }}>
              <div className="flex items-center gap-3">
                <Truck size={18} style={{ color: t.ready ? '#1F9D7A' : '#C58B19' }}/>
                <div>
                  <p className="font-semibold text-sm" style={{ color: 'var(--admin-text)' }}>{t.nom}</p>
                  <p className="text-xs" style={{ color: 'var(--admin-faint)' }}>{t.desc}</p>
                </div>
              </div>
              <span className="text-xs px-2 py-1 rounded-full"
                style={{ background: t.ready ? 'rgba(31,157,122,0.1)' : 'rgba(197,139,25,0.1)', color: t.ready ? '#1F9D7A' : '#C58B19' }}>
                {t.ready ? 'Actif' : 'API à connecter'}
              </span>
            </div>
          ))}
          <div className="p-4 rounded-2xl" style={{ background: 'rgba(125,177,255,0.06)', border: '1px solid rgba(125,177,255,0.2)' }}>
            <p className="text-xs" style={{ color: 'var(--admin-muted)', lineHeight: 1.7 }}>
              L'architecture est prête. Pour activer :<br/>
              · Mondial Relay : clé API + code enseigne<br/>
              · Chronopost : login + MDP + compte client<br/>
              · Colissimo : login + MDP + contrat<br/>
              Frais calculés en temps réel une fois les clés configurées.
            </p>
          </div>
        </div>
      )}

      {selectedPage && (
        <SEOPageForm pageConfig={selectedPage} existingData={pages.find(p => p.page_id === selectedPage.id)}
          onSave={() => { setSelectedPage(null); load(); }} onCancel={() => setSelectedPage(null)} />
      )}
      </div>
    </div>
  );
}
